import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { registerModalBackHandler } from '../lib/native';
import { Spinner } from './design-system';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export function FaqModal({ audience, onClose }: { audience: 'traveler' | 'host'; onClose: () => void }) {
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from('faqs')
      .select('id, question, answer')
      .eq('published', true)
      .or(`audience.eq.${audience},audience.eq.both`)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        setFaqs((data as FaqItem[]) ?? []);
        setLoading(false);
      });
  }, [audience]);

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-dvh-80 bg-app-card rounded-2xl shadow-2xl border border-app-border flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border shrink-0">
          <h2 className="text-lg font-semibold text-app-fg">Frequently Asked Questions</h2>
          <button onClick={onClose} className="text-app-muted hover:text-app-fg transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : faqs.length === 0 ? (
            <p className="text-center text-app-muted text-sm py-8">No FAQs available yet.</p>
          ) : (
            <div className="space-y-2">
              {faqs.map((faq) => (
                <div key={faq.id} className="rounded-xl border border-app-border overflow-hidden">
                  <button
                    onClick={() => setOpen((cur) => cur === faq.id ? null : faq.id)}
                    className="flex items-center justify-between w-full px-4 py-3 text-left text-sm font-medium text-app-fg hover:bg-app-bg/50 transition-colors"
                  >
                    <span>{faq.question}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 shrink-0 text-app-muted transition-transform ${open === faq.id ? 'rotate-180' : ''}`}>
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {open === faq.id && (
                    <div className="px-4 pb-3 text-sm text-app-muted whitespace-pre-wrap">{faq.answer}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
